import React, { useEffect, useRef, useCallback } from 'react';
import './Parcours.css';
import useAutoTranslate from '../../hooks/useAutoTranslate';
import { pub } from '../../utils/Publiccurl.js';
import fr from './langue/fr.json';
import en from './langue/en.json';
import {
  createAnimState,
  resizeCanvas,
  flushAnimState,
  updatePacket,
  drawPacket,
  spawnPing,
  updatePings,
  drawPings,
  spawnPulse,
  updatePulses,
  drawPulses,
  drawLine,
  maybeSpawn,
} from '../../utils/Timelineanimation.js';

const MOBILE_BP = 640;

/* ── Données statiques (assets uniquement, les textes viennent du JSON) ── */
const parcoursMedia = [
  {
    img: 'assets/img/parcours/isen.png',
    certs: [
      { href: 'assets/certificat_toeic.pdf', img: 'assets/img/techno/toeic.png', alt: 'TOEIC' },
      { href: 'assets/Label%20Handimanagement.pdf', img: 'assets/img/techno/lh.png', alt: 'Label Handi' },
    ],
  },
  { img: 'assets/img/parcours/udemy.png' },
  { img: 'assets/img/parcours/anssi.png' },
  { img: 'assets/img/parcours/iut.png', techs: ['python', 'plsql', 'java', 'linux', 'php', 'laravel', 'git', 'reseau'] },
  { img: 'assets/img/parcours/ulille.png' },
  { img: 'assets/img/parcours/med.png' },
  { img: 'assets/img/parcours/beaupre.png' },
];

/* ── Icône RJ45 ── */
const RJ45Dot = React.memo(({ index }) => (
  <div className="timeline__connector" data-index={index} aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      fill="none"
      stroke="#58a6ff"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="18" y="28" width="28" height="18" rx="3" fill="rgba(88,166,255,0.1)" stroke="#58a6ff" />
      <path d="M24 28 L24 22 Q24 18 28 18 L36 18 Q40 18 40 22 L40 28" />
      <line x1="24" y1="32" x2="24" y2="44" />
      <line x1="28" y1="32" x2="28" y2="44" />
      <line x1="32" y1="32" x2="32" y2="44" />
      <line x1="36" y1="32" x2="36" y2="44" />
      <line x1="40" y1="32" x2="40" y2="44" />
      <line x1="32" y1="18" x2="32" y2="8" />
    </svg>
  </div>
));

/* ── Canvas réseau ── */
function NetworkCanvas({ timelineRef }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);
  if (!stateRef.current) stateRef.current = createAnimState();

  const flashItem = useCallback((idx) => {
    const items = timelineRef.current?.querySelectorAll('.timeline__item');
    if (!items?.[idx]) return;
    const card = items[idx].querySelector('.timeline__content');
    const conn = items[idx].querySelector('.timeline__connector');
    [card, conn].forEach((el) => {
      if (!el) return;
      const cls = el === card ? 'timeline__content--flash' : 'timeline__connector--active';
      el.classList.add(cls);
      setTimeout(() => el.classList.remove(cls), 600);
    });
  }, [timelineRef]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const state  = stateRef.current;

    const onConnectorHit = (c) => {
      spawnPing(state, c);
      spawnPulse(state, c);
      flashItem(c.index);
    };

    const loop = (now) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawLine(ctx, canvas, state);
      maybeSpawn(now, state);
      state.packets.forEach((p) => updatePacket(p, canvas, state, onConnectorHit));
      state.packets.forEach((p) => drawPacket(ctx, p));
      state.packets = state.packets.filter((p) => !p.dead);
      updatePings(state);  drawPings(ctx, state);
      updatePulses(state); drawPulses(ctx, state);
      state.animId = requestAnimationFrame(loop);
    };

    const resize = () => resizeCanvas(canvas, timelineRef, state);

    let prevMobile = window.innerWidth <= MOBILE_BP;
    const handleResize = () => {
      const nowMobile = window.innerWidth <= MOBILE_BP;
      if (nowMobile !== prevMobile) { flushAnimState(state); prevMobile = nowMobile; }
      resize();
    };

    resize();
    const ro = new ResizeObserver(handleResize);
    if (timelineRef.current) ro.observe(timelineRef.current);

    const startTimer = setTimeout(() => {
      resize();
      state.animId = requestAnimationFrame(loop);
    }, 300);

    const restartInterval = setInterval(() => {
      cancelAnimationFrame(state.animId);
      flushAnimState(state);
      resize();
      state.lastSpawn = 0;
      state.animId = requestAnimationFrame(loop);
    }, 60000);

    return () => {
      cancelAnimationFrame(state.animId);
      clearInterval(restartInterval);
      clearTimeout(startTimer);
      ro.disconnect();
      flushAnimState(state);
      stateRef.current = createAnimState();
    };
  }, [flashItem, timelineRef]);

  return <canvas ref={canvasRef} className="timeline__canvas" aria-hidden="true" />;
}

/* ── Item timeline ── */
const TimelineItem = React.memo(({ item, media, index, t }) => (
  <div className={`timeline__item${index % 2 === 1 ? ' timeline__item--right' : ''}`}>
    <div className="timeline__logo">
      <img src={pub(media.img)} alt={item.org} loading="lazy" width="64" height="64" />
    </div>
    <RJ45Dot index={index} />
    <div className="timeline__content">
      <div className="timeline__logo--inline">
        <img src={pub(media.img)} alt={item.org} loading="lazy" width="40" height="40" />
      </div>
      <span className="timeline__period">{item.period}</span>
      <h3>{item.title}</h3>
      <p className="timeline__org">{item.org}</p>
      <p className="timeline__desc">{item.desc}</p>

      {media.techs && (
        <div className="timeline__techs" aria-label={t('aria.techs')}>
          {media.techs.map((tech) => (
            <img key={tech} src={pub(`assets/img/techno/${tech}.png`)} alt={tech} loading="lazy" width="32" height="32" />
          ))}
        </div>
      )}

      {media.certs && (
        <div className="timeline__techs" aria-label={t('aria.certs')}>
          {media.certs.map((c) => (
            <a
              key={c.alt}
              href={pub(c.href)}
              target="_blank"
              rel="noreferrer"
              aria-label={`${t('aria.certLink')} ${c.alt}`}
            >
              <img src={pub(c.img)} alt={c.alt} loading="lazy" width="32" height="32" />
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
));

/* ── Composant principal ── */
function Parcours() {
  const timelineRef = useRef(null);
  const t           = useAutoTranslate({ fr, en });
  const raw         = t('items');
  const items       = Array.isArray(raw) ? raw : [];
  const animKey     = t('heading');

  return (
    <section className="parcours" id="parcours" aria-labelledby="parcours-heading">
      <div className="section-container">
        <h2 id="parcours-heading" className="section-heading">{t('heading')}</h2>

        <div className="timeline" ref={timelineRef}>
          <div className="timeline__line" aria-hidden="true" />
          <NetworkCanvas key={animKey} timelineRef={timelineRef} />

          {items.map((item, index) => (
            <TimelineItem
              key={index}
              item={item}
              media={parcoursMedia[index]}
              index={index}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Parcours;